import {FC, useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {useAuth} from '../../modules/auth'
import ReactApexChart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import {api, Bed, OccupancySummary, BedStatusValue, DashboardStats} from '../../services/api'
import {BED_INVENTORY, BedType} from '../../types/domain'
import {PageHead} from '../../components/PageHead'
import {useIsMobile} from '../../hooks/useIsMobile'

/**
 * Crew Crashpad — Admin dashboard. Live operational overview: real bed
 * occupancy, pending applications and bookings, plus the build roadmap.
 */

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const BED_TYPE_LABEL: Record<BedType, string> = {
  [BedType.TWIN]: 'Twin',
  [BedType.FULL]: 'Full',
  [BedType.QUEEN]: 'Queen',
}

const BED_TYPE_TONE: Record<string, string> = {
  TWIN: '#039540',
  FULL: '#7c4dff',
  QUEEN: '#0aa37f',
}

const BED_STATUS_META: Record<BedStatusValue, {label: string; color: string; bg: string}> = {
  AVAILABLE: {label: 'Available', color: '#0aa37f', bg: 'rgba(10,163,127,0.13)'},
  OCCUPIED: {label: 'Occupied', color: '#2b4eff', bg: 'rgba(43,78,255,0.12)'},
  RESERVED: {label: 'Reserved', color: '#b8770a', bg: 'rgba(255,168,0,0.16)'},
  OUT_OF_SERVICE: {label: 'Out of svc', color: '#7e8299', bg: 'rgba(126,130,153,0.15)'},
}

interface Kpi {
  label: string
  value: string
  hint: string
  to?: string
  cta?: string
}

const KpiCard: FC<Kpi> = ({label, value, hint, to, cta}) => {
  const isMobile = useIsMobile()
  const body = (
    <div className='card-body'>
      <div style={{fontSize: 11, fontWeight: 600, letterSpacing: isMobile ? '0.04em' : '0.1em', textTransform: 'uppercase', color: MUTED}}>
        {label}
      </div>
      <div style={{fontSize: isMobile ? 25 : 30, fontWeight: 800, color: INK, lineHeight: 1.1, marginTop: 6, whiteSpace: 'nowrap'}}>{value}</div>
      <div style={{fontSize: 12, color: MUTED, marginTop: 4}}>{hint}</div>
      {to && <div style={{fontSize: 12, fontWeight: 700, color: ACCENT, marginTop: 8}}>{cta ?? 'Manage'} →</div>}
    </div>
  )
  const cardStyle: React.CSSProperties = {borderRadius: 14, border: 'none', boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}
  return to ? (
    <Link to={to} className='card h-100 ccp-lift' style={{...cardStyle, textDecoration: 'none', display: 'block'}}>{body}</Link>
  ) : (
    <div className='card h-100 ccp-lift' style={cardStyle}>{body}</div>
  )
}

const ChartCard: FC<{title: string; subtitle: string; children: React.ReactNode}> = ({title, subtitle, children}) => (
  <div className='card h-100 ccp-lift' style={{borderRadius: 16, border: 'none', boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
    <div className='card-header border-0 pt-5'>
      <div>
        <h3 style={{fontSize: 18, fontWeight: 700, color: INK, margin: 0}}>{title}</h3>
        <span style={{fontSize: 12, color: MUTED}}>{subtitle}</span>
      </div>
    </div>
    <div className='card-body pt-2'>{children}</div>
  </div>
)

const areaOptions = (categories: string[]): ApexOptions => ({
  chart: {toolbar: {show: false}, sparkline: {enabled: false}, fontFamily: 'inherit'},
  colors: [ACCENT],
  dataLabels: {enabled: false},
  stroke: {curve: 'smooth', width: 3},
  fill: {type: 'gradient', gradient: {shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 95]}},
  grid: {borderColor: '#eff2f5', strokeDashArray: 4},
  xaxis: {categories, axisBorder: {show: false}, axisTicks: {show: false}, labels: {style: {colors: MUTED, fontSize: '12px'}}},
  yaxis: {labels: {style: {colors: MUTED, fontSize: '12px'}, formatter: (v) => `$${Math.round(v)}`}},
  tooltip: {y: {formatter: (v) => `$${v.toLocaleString()}`}},
})

const barOptions = (categories: string[]): ApexOptions => ({
  chart: {toolbar: {show: false}, fontFamily: 'inherit'},
  colors: [ACCENT],
  dataLabels: {enabled: false},
  plotOptions: {bar: {borderRadius: 6, columnWidth: '45%'}},
  grid: {borderColor: '#eff2f5', strokeDashArray: 4},
  xaxis: {categories, axisBorder: {show: false}, axisTicks: {show: false}, labels: {style: {colors: MUTED, fontSize: '12px'}}},
  yaxis: {labels: {style: {colors: MUTED, fontSize: '12px'}, formatter: (v) => String(Math.round(v))}},
})

const donutOptions = (labels: string[], colors: string[]): ApexOptions => ({
  chart: {fontFamily: 'inherit'},
  labels,
  colors,
  legend: {position: 'bottom', fontSize: '13px', labels: {colors: MUTED}, markers: {radius: 12}},
  dataLabels: {enabled: false},
  stroke: {width: 0},
  plotOptions: {pie: {donut: {size: '68%', labels: {show: true, total: {show: true, label: 'Total', color: MUTED, formatter: (w) => String(w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0))}}}}},
})

const AdminDashboard: FC = () => {
  const {currentUser} = useAuth()
  const totalBeds = BED_INVENTORY.length

  const [beds, setBeds] = useState<Bed[]>([])
  const [summary, setSummary] = useState<OccupancySummary | null>(null)
  const [stats, setStats] = useState<DashboardStats | null>(null)

  useEffect(() => {
    const swallow = () => undefined
    api.listBeds().then(setBeds).catch(swallow)
    api.bedsSummary().then(setSummary).catch(swallow)
    api.dashboardStats().then(setStats).catch(swallow)
  }, [])

  // Real bed data once loaded; fall back to the inventory (all available) meanwhile.
  const bedRows: {code: string; type: string; status: BedStatusValue; occupant: string | null}[] = beds.length
    ? beds.map((b) => ({code: b.code, type: b.type, status: b.status, occupant: b.occupant ? `${b.occupant.firstName} ${b.occupant.lastName}` : null}))
    : BED_INVENTORY.map((b) => ({code: b.code, type: b.type as string, status: 'AVAILABLE' as BedStatusValue, occupant: null}))

  const n = (v: number | null | undefined) => (v == null ? '—' : String(v))
  const c = stats?.counts
  const kpis: Kpi[] = [
    {label: 'Revenue', value: stats ? `$${stats.revenue.total.toLocaleString()}` : '—', hint: 'Collected to date', to: '/payments', cta: 'View payments'},
    {label: 'Occupied beds', value: stats ? `${c!.occupiedBeds}/${c!.beds}` : n(summary?.occupied), hint: 'Currently in use', to: '/beds', cta: 'Manage beds'},
    {label: 'Verified renters', value: n(c?.verifiedRenters), hint: c ? `of ${c.renters} signed up` : 'Approved crew'},
    {label: 'Pending applications', value: n(c?.pendingApplications), hint: 'Awaiting review', to: '/applications', cta: 'Review'},
    {label: 'Pending bookings', value: n(c?.pendingBookings), hint: 'Awaiting approval', to: '/bookings', cta: 'Review'},
    {label: 'Open requests', value: n(c?.openRequests), hint: 'Cleaning & maintenance', to: '/requests', cta: 'Triage'},
  ]

  const monthLabel = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, {month: 'short'})

  return (
    <div>
      {/* Header */}
      <PageHead
        icon='🛫'
        eyebrow='Crew Crashpad'
        title={`Welcome back${currentUser?.first_name ? `, ${currentUser.first_name}` : ''}`}
        sub="Here's your crashpad at a glance."
        action={
          <span style={{background: 'rgba(255,255,255,0.16)', color: '#fff', borderRadius: 999, padding: '8px 16px', fontWeight: 700, fontSize: 13, letterSpacing: '0.04em'}}>
            🛫 crosscheckcrashpad.com
          </span>
        }
      />

      {/* KPI row */}
      <div className='row g-4 mb-6 ccp-stagger'>
        {kpis.map((k) => (
          <div className='col-12 col-sm-6 col-md-4 col-xl' key={k.label}>
            <KpiCard {...k} />
          </div>
        ))}
      </div>

      {/* Charts */}
      {stats && (
        <div className='row g-4 mb-6 ccp-fade-up-2'>
          <div className='col-12 col-xl-8'>
            <ChartCard title='Revenue' subtitle='Collected over the last 6 months'>
              <ReactApexChart
                type='area'
                height={300}
                series={[{name: 'Revenue', data: stats.revenue.byMonth.map((m) => m.amount)}]}
                options={areaOptions(stats.revenue.byMonth.map((m) => monthLabel(m.month)))}
              />
            </ChartCard>
          </div>
          <div className='col-12 col-xl-4'>
            <ChartCard title='Bed occupancy' subtitle={`${c!.occupiedBeds} of ${c!.beds} beds in use`}>
              <ReactApexChart
                type='donut'
                height={300}
                series={[stats.occupancy.AVAILABLE, stats.occupancy.OCCUPIED, stats.occupancy.RESERVED, stats.occupancy.OUT_OF_SERVICE]}
                options={donutOptions(['Available', 'Occupied', 'Reserved', 'Out of service'], ['#1f9d5b', '#2b4eff', '#b8770a', '#9aa0ad'])}
              />
            </ChartCard>
          </div>
          <div className='col-12 col-xl-8'>
            <ChartCard title='Bookings' subtitle='New requests per month'>
              <ReactApexChart
                type='bar'
                height={280}
                series={[{name: 'Bookings', data: stats.bookingsByMonth.map((m) => m.count)}]}
                options={barOptions(stats.bookingsByMonth.map((m) => monthLabel(m.month)))}
              />
            </ChartCard>
          </div>
          <div className='col-12 col-xl-4'>
            <ChartCard title='Requests' subtitle='Cleaning & maintenance by status'>
              <ReactApexChart
                type='donut'
                height={280}
                series={[stats.requestsByStatus.OPEN, stats.requestsByStatus.IN_PROGRESS, stats.requestsByStatus.RESOLVED, stats.requestsByStatus.CLOSED]}
                options={donutOptions(['Open', 'In progress', 'Resolved', 'Closed'], ['#b8770a', '#2b4eff', '#1f9d5b', '#9aa0ad'])}
              />
            </ChartCard>
          </div>
        </div>
      )}

      <div className='row g-6 ccp-fade-up-2'>
        {/* Bed board */}
        <div className='col-12'>
          <div className='card' style={{borderRadius: 16, border: 'none', boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
            <div className='card-header border-0 pt-5 d-flex align-items-center justify-content-between'>
              <div>
                <h3 style={{fontSize: 18, fontWeight: 700, color: INK, margin: 0}}>Bed Board</h3>
                <span style={{fontSize: 12, color: MUTED}}>Live occupancy across all {totalBeds} beds</span>
              </div>
              <Link to='/beds' style={{color: ACCENT, fontWeight: 600, fontSize: 13, textDecoration: 'none'}}>Manage →</Link>
            </div>
            <div className='card-body pt-4'>
              <div className='row g-3'>
                {bedRows.map((bed) => {
                  const sm = BED_STATUS_META[bed.status]
                  return (
                    <div className='col-6 col-md-3' key={bed.code}>
                      <div
                        style={{
                          borderRadius: 12,
                          border: '1px solid rgba(126,130,153,0.18)',
                          borderLeft: `5px solid ${BED_TYPE_TONE[bed.type] ?? ACCENT}`,
                          padding: '14px 16px',
                          background: '#fff',
                        }}
                      >
                        <div style={{fontSize: 24, fontWeight: 800, color: INK, lineHeight: 1}}>{bed.code}</div>
                        <div style={{fontSize: 12, color: MUTED, marginTop: 4}}>{BED_TYPE_LABEL[bed.type as BedType] ?? bed.type}</div>
                        <span style={{display: 'inline-block', marginTop: 10, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: sm.color, background: sm.bg, borderRadius: 999, padding: '3px 9px'}}>
                          {sm.label}
                        </span>
                        {bed.occupant && <div style={{fontSize: 11, color: MUTED, marginTop: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{bed.occupant}</div>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const AdminDashboardWrapper: FC = () => <AdminDashboard />

export {AdminDashboardWrapper}
